/**
 * /social-done — Final landing point after OAuth callback.
 *
 * Backend redirects here with: ?token=<JWT>&new_user=0|1&prefill=<b64>&provider=...
 *
 * For new users we additionally:
 *  - Replay UTM + referral_code (captured in sessionStorage before the OAuth redirect)
 *    via POST /api/auth/oauth-onboarding (idempotent server-side).
 *  - Land directly on /dashboard with OAuth-prefilled basics already attached.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertCircle, Link2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = (typeof window !== 'undefined' && window.__MIU_ACTIVE_BACKEND_URL__) || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const b64urlDecode = (s) => {
  if (!s) return null;
  try {
    const pad = '='.repeat((4 - (s.length % 4)) % 4);
    const b64 = (s + pad).replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
};

const SocialDone = () => {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const searchParams = new URLSearchParams(window.location.search);
  const tokenFromUrl = searchParams.get('token');
  const isNewUserFromUrl = searchParams.get('new_user') === '1';
  const prefillFromUrl = searchParams.get('prefill');
  const oauthErrorFromUrl = searchParams.get('oauth_error');
  const [error, setError] = useState(() => oauthErrorFromUrl || '');
  const [phase, setPhase] = useState('loading'); // loading | merge-prompt | merging
  const [mergeEmail, setMergeEmail] = useState('');
  const [mergeOtp, setMergeOtp] = useState('');
  const [mergeStage, setMergeStage] = useState('idle'); // idle | otp-sent
  const [mergeBusy, setMergeBusy] = useState(false);
  const ctxRef = useRef({ token: '', isNewUser: false });
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    if (error) {
      return;
    }

    let ignore = false;

    const completeSocialSignIn = async () => {
      const prefill = b64urlDecode(prefillFromUrl);
      if (prefill && (prefill.headline || prefill.name)) {
        try {
          sessionStorage.setItem('linkedin_prefill', JSON.stringify(prefill));
        } catch (storageError) {
          console.warn('Could not store LinkedIn prefill', storageError);
        }
      }

      try {
        let resolvedToken = tokenFromUrl;
        let resolvedRefreshToken = null;

        if (!resolvedToken) {
          const refreshResp = await axios.post(`${API}/auth/refresh`, {}, {
            withCredentials: true,
            timeout: 10000,
          });
          resolvedToken = refreshResp.data?.access_token || '';
          resolvedRefreshToken = refreshResp.data?.refresh_token || null;
        }

        if (!resolvedToken) {
          throw new Error('No authenticated session found after social sign-in.');
        }

        ctxRef.current = { token: resolvedToken, isNewUser: isNewUserFromUrl };

        // 1. Fetch profile + log in
        const resp = await axios.get(`${API}/profile`, {
          headers: { Authorization: `Bearer ${resolvedToken}` },
          withCredentials: true,
          timeout: 10000,
        });

        // 2. Replay tracking params (idempotent server-side)
        if (isNewUserFromUrl) {
          let tracking = null;
          try {
            tracking = JSON.parse(sessionStorage.getItem('miu_signup_tracking') || 'null');
          } catch (trackingError) {
            console.warn('Could not parse signup tracking', trackingError);
          }
          if (tracking) {
            try {
              await axios.post(`${API}/auth/oauth-onboarding`, { tracking }, {
                headers: { Authorization: `Bearer ${resolvedToken}` },
                withCredentials: true,
                timeout: 8000,
              });
            } catch (e) {
              // Non-blocking — onboarding can be retried
              console.warn('oauth-onboarding replay failed', e);
            }
            try {
              sessionStorage.removeItem('miu_signup_tracking');
            } catch (removeError) {
              console.warn('Could not clear signup tracking', removeError);
            }
          }
        }

        if (ignore) return; // Component unmounted — skip state updates
        loginWithToken(resolvedToken, resp.data, resolvedRefreshToken);

        // 3. Frictionless social onboarding:
        setPhase('loading');
        navigate('/dashboard', { replace: true });
      } catch (err) {
        if (ignore) return;
        navigate('/login', {
          replace: true,
          state: {
            error: err?.response?.data?.detail || err?.response?.data?.error?.message || err?.message || 'Failed to complete sign-in.'
          }
        });
      }
    };

    completeSocialSignIn();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const skipMerge = () => {
    navigate('/dashboard', { replace: true });
  };

  const sendMergeOtp = async () => {
    if (!mergeEmail.trim()) return;
    setMergeBusy(true);
    try {
      await axios.post(
        `${API}/auth/merge-with-existing/start`,
        { other_email: mergeEmail.trim().toLowerCase() },
        {
          withCredentials: true,
          headers: ctxRef.current.token ? { Authorization: `Bearer ${ctxRef.current.token}` } : undefined,
        }
      );
      setMergeStage('otp-sent');
      toast.success('Verification code sent. Check that email inbox.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.response?.data?.error?.message || 'Could not send code. Try again.');
    } finally {
      setMergeBusy(false);
    }
  };

  const confirmMerge = async () => {
    if (!mergeOtp.trim()) return;
    setMergeBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/merge-with-existing/confirm`,
        { other_email: mergeEmail.trim().toLowerCase(), otp: mergeOtp.trim() },
        {
          withCredentials: true,
          headers: ctxRef.current.token ? { Authorization: `Bearer ${ctxRef.current.token}` } : undefined,
        }
      );
      toast.success('Accounts merged — welcome back!');
      // Switch the session to the target account
      const newToken = data.token;
      const profile = await axios.get(`${API}/profile`, {
        withCredentials: true,
        headers: newToken ? { Authorization: `Bearer ${newToken}` } : undefined,
      });
      loginWithToken(newToken, profile.data);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.response?.data?.error?.message || 'Invalid code.');
    } finally {
      setMergeBusy(false);
    }
  };

  // ─────────── error UI ───────────
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 flex items-center justify-center px-4" data-testid="social-done-error">
        <div className="max-w-sm w-full bg-white rounded-3xl shadow-xl border border-slate-100 p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Sign-in Failed</h2>
          <p className="text-sm text-slate-500 mb-6">{error}</p>
          <button
            data-testid="social-done-back-btn"
            onClick={() => navigate('/login', { replace: true })}
            className="w-full bg-[#E8430A] hover:bg-[#d13a08] text-white rounded-full px-8 py-3 font-semibold transition-all"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // ─────────── merge prompt UI (new social user only) ───────────
  if (phase === 'merge-prompt') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 flex items-center justify-center px-4" data-testid="social-done-merge-prompt">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl border border-slate-100 p-8">
          <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mb-4">
            <Link2 className="w-6 h-6 text-blue-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-1">Welcome to Match It Up!</h2>
          <p className="text-sm text-slate-600 mb-5">
            Already have a Match It Up account with a different email? Link it here so you don&apos;t lose your matches, contracts and credits.
          </p>

          {mergeStage === 'idle' && (
            <>
              <label className="block text-xs font-medium text-slate-700 mb-1">Your existing account email</label>
              <input
                type="email"
                value={mergeEmail}
                onChange={(e) => setMergeEmail(e.target.value)}
                placeholder="you@oldemail.com"
                data-testid="merge-email-input"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
              />
              <button
                onClick={sendMergeOtp}
                disabled={mergeBusy || !mergeEmail.trim()}
                data-testid="merge-send-otp-btn"
                className="w-full mt-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl px-4 py-2.5 font-medium text-sm transition-colors"
              >
                {mergeBusy ? 'Sending…' : 'Send verification code'}
              </button>
            </>
          )}

          {mergeStage === 'otp-sent' && (
            <>
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 mb-3">
                We sent a 6-digit code to <strong>{mergeEmail}</strong>. Enter it below to merge.
              </p>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={mergeOtp}
                onChange={(e) => setMergeOtp(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                placeholder="000000"
                data-testid="merge-otp-input"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-center tracking-[0.5em] font-mono text-lg focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
              />
              <button
                onClick={confirmMerge}
                disabled={mergeBusy || mergeOtp.length !== 6}
                data-testid="merge-confirm-btn"
                className="w-full mt-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl px-4 py-2.5 font-medium text-sm transition-colors"
              >
                {mergeBusy ? 'Merging…' : 'Confirm merge'}
              </button>
              <button
                onClick={() => { setMergeStage('idle'); setMergeOtp(''); }}
                className="w-full mt-1 text-xs text-slate-500 hover:text-slate-700 py-1"
              >
                Use a different email
              </button>
            </>
          )}

          <button
            onClick={skipMerge}
            data-testid="merge-skip-btn"
            className="w-full mt-4 text-sm text-slate-500 hover:text-slate-700 flex items-center justify-center gap-1"
          >
            No, continue with a new profile <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // ─────────── default loading UI ───────────
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 flex items-center justify-center" data-testid="social-done-loading">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-[#E8430A] animate-spin mx-auto mb-4" />
        <p className="text-slate-600 font-medium">Completing sign-in…</p>
        <p className="text-slate-400 text-sm mt-1">Please wait a moment…</p>
      </div>
    </div>
  );
};

export default SocialDone;
